/**
 * Iter140l — Utility per la condivisione nativa dei file su mobile/desktop.
 *
 * Strategia (in ordine di preferenza):
 *   1. `navigator.share({files})` con il file reale scaricato al volo.
 *      → Su iOS/Android apre lo sheet nativo (WhatsApp, AirDrop, Mail, ...).
 *   2. `navigator.share({url})` con solo il link firmato.
 *      → Fallback per browser che supportano share URL ma non file.
 *   3. `navigator.clipboard.writeText(url)` come ultima spiaggia.
 *
 * NB: il signed URL viene sempre RIGENERATO al momento della condivisione
 * chiamando `GET /api/files/{key}/signed-url` (TTL 15 min lato backend),
 * così il destinatario non riceve mai un link scaduto.
 */
import { api } from "@/lib/api";
import { toast } from "sonner";

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Estrae la file_key da un URL Housetag del tipo `/api/files/{key}` o
 * `{origin}/api/files/{key}?sig=...`. Ritorna null se non riconosciuto.
 */
function extractFileKey(url) {
  if (!url) return null;
  const m = String(url).match(/\/api\/files\/([^/?]+)/);
  return m ? m[1] : null;
}

/**
 * Chiede al backend un signed URL fresco (TTL 15min).
 * Ritorna l'URL assoluto pronto per il download.
 */
async function refreshSignedUrl(fileKey) {
  const r = await api.get(`/files/${fileKey}/signed-url`);
  const relative = r.data?.url;
  if (!relative) throw new Error("Nessun URL restituito dal server");
  return relative.startsWith("http") ? relative : `${API_ORIGIN}${relative}`;
}

/**
 * Scarica un file come Blob dal signed URL fresco.
 * Ritorna { blob, filename, mime, url }.
 */
async function fetchFileBlob(fileKey, filename) {
  const url = await refreshSignedUrl(fileKey);
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Download fallito (HTTP ${resp.status})`);
  const blob = await resp.blob();
  return {
    blob,
    filename: filename || fileKey,
    mime: blob.type || "application/octet-stream",
    url,
  };
}

/**
 * API pubblica: condivide un file.
 * @param {object} params
 * @param {string} params.url         URL del file (relativo o assoluto).
 * @param {string} params.filename    Nome del file per WhatsApp/download.
 * @param {string} [params.title]     Titolo dell'operazione di share.
 * @param {string} [params.text]      Testo accompagnatorio.
 */
export async function shareFile({ url, filename, title, text }) {
  const key = extractFileKey(url);
  if (!key) {
    // File non firmato Housetag → copia semplice URL.
    return copyToClipboard(url, filename);
  }

  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  const canShareFiles = canShare && typeof navigator.canShare === "function";

  // 1) Prova con FILE reale (mobile-first, apre share sheet OS).
  if (canShareFiles) {
    try {
      const { blob, filename: fn, mime, url: fresh } = await fetchFileBlob(key, filename);
      const file = new File([blob], fn, { type: mime });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: title || fn,
            text: text || "",
          });
          return { ok: true, method: "files" };
        } catch (err) {
          // AbortError (utente chiude sheet) → non è un fallimento.
          if (err?.name === "AbortError") return { ok: false, method: "aborted" };
          console.warn("[shareFile] share files failed, trying URL fallback:", err);
        }
      }
      // 2) Fallback: share solo con URL (link firmato fresco).
      try {
        await navigator.share({
          url: fresh,
          title: title || fn,
          text: text ? `${text}\n${fresh}` : fresh,
        });
        return { ok: true, method: "url" };
      } catch (err) {
        if (err?.name === "AbortError") return { ok: false, method: "aborted" };
        console.warn("[shareFile] share url failed, trying clipboard:", err);
        return copyToClipboard(fresh, fn);
      }
    } catch (err) {
      console.warn("[shareFile] file fetch failed:", err);
      // fall through al fallback URL-only con link fresco (senza blob).
    }
  }

  // 3) Solo URL share (browser mobile che non supportano files array).
  try {
    const fresh = await refreshSignedUrl(key);
    if (canShare) {
      try {
        await navigator.share({
          url: fresh,
          title: title || filename,
          text: text ? `${text}\n${fresh}` : fresh,
        });
        return { ok: true, method: "url" };
      } catch (err) {
        if (err?.name === "AbortError") return { ok: false, method: "aborted" };
      }
    }
    return copyToClipboard(fresh, filename);
  } catch (err) {
    // Iter151 — Ripiego esplicito: invece del solo toast di errore, offri
    // all'utente due azioni immediate ("Copia Link" oppure "Scarica")
    // usando il fresh signed URL. Se anche il refresh è fallito, prova con
    // l'URL originale (potenzialmente ancora valido).
    console.error("[shareFile] refresh failed:", err);
    return showFallbackActions({ originalUrl: url, filename });
  }
}


/**
 * Iter151 — Ripiego UI: mostra un toast persistente con due bottoni
 * "Copia Link" e "Scarica" quando né navigator.share né il refresh URL
 * funzionano. L'utente ha così un'alternativa immediata senza dover
 * ricaricare o riaprire il modale.
 */
async function showFallbackActions({ originalUrl, filename }) {
  const absoluteUrl = originalUrl && originalUrl.startsWith("http")
    ? originalUrl
    : `${API_ORIGIN}${originalUrl || ""}`;
  toast.error("Condivisione non riuscita", {
    description: "Puoi comunque copiare il link o scaricare il file.",
    duration: 8000,
    action: {
      label: "Copia link",
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(absoluteUrl);
          toast.success("Link copiato negli appunti");
        } catch {
          window.open(absoluteUrl, "_blank", "noopener,noreferrer");
        }
      },
    },
    cancel: {
      label: "Scarica",
      onClick: () => {
        // Force download via anchor con attributo download
        const a = document.createElement("a");
        a.href = absoluteUrl;
        a.download = filename || "";
        a.rel = "noopener noreferrer";
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        a.remove();
      },
    },
  });
  return { ok: false, method: "fallback-actions" };
}

/**
 * Ultima spiaggia: copia link negli appunti.
 */
async function copyToClipboard(url, filename) {
  try {
    await navigator.clipboard.writeText(url);
    toast.success(`Link ${filename || ""} copiato — incollalo dove vuoi condividerlo.`);
    return { ok: true, method: "clipboard" };
  } catch (e) {
    // Fallback finale: apri in nuova tab.
    window.open(url, "_blank", "noopener,noreferrer");
    toast.info("Link aperto in nuova scheda — copia manualmente per condividere.");
    return { ok: true, method: "openTab" };
  }
}
